import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle2, XCircle, PauseCircle, RefreshCw } from "lucide-react";
import { PoweredByFundle } from "../components/Brand";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STATUSES = [
  { key: "selected", label: "Selected", icon: CheckCircle2, color: "bg-emerald-600" },
  { key: "rejected", label: "Rejected", icon: XCircle, color: "bg-red-600" },
  { key: "hold", label: "On Hold", icon: PauseCircle, color: "bg-amber-500" },
  { key: "need_another_round", label: "Need Another Round", icon: RefreshCw, color: "bg-[#0A251D]" },
];

export default function ExternalDecision() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [happened, setHappened] = useState(true);
  const [status, setStatus] = useState("selected");
  const [rating, setRating] = useState(4);
  const [salary, setSalary] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/automation/external-decision/${token}`)
      .then((r) => { setData(r.data); if (r.data.submitted) setDone(true); })
      .catch(() => toast.error("This decision link is invalid or expired"));
  }, [token]);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { token, interview_happened: happened, status, rating: +rating,
                     recommended_salary: salary ? +salary : null, remarks };
      await axios.post(`${API}/automation/external-decision/submit`, body);
      toast.success("Decision recorded — thank you!");
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit decision");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F9F9F6] py-10 px-4" data-testid="external-decision-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-2xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">External Interviewer</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-1">Share Your Decision</h1>
        <p className="text-sm text-muted-foreground mt-1">Hi {data.interviewer_name} — please capture the outcome of the {data.round_name} for {data.candidate.name}.</p>

        <div className="border bg-white rounded-xl p-6 mt-6">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2">Candidate</div>
          <div className="text-lg font-semibold">{data.candidate.name}</div>
          <div className="text-xs text-muted-foreground">{data.candidate.email} · {data.candidate.experience_years} yrs · {data.candidate.current_company || "—"}</div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-1">Role</div>
          <div className="text-sm font-semibold">{data.requisition.title}</div>
          <div className="text-xs text-muted-foreground">Skills: {data.requisition.skills}</div>
        </div>

        {done ? (
          <div className="border-2 border-emerald-600 bg-white rounded-xl p-6 mt-6 text-center" data-testid="decision-done">
            <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
            <div className="font-heading font-bold text-lg mt-3">Decision received</div>
            <div className="text-xs text-muted-foreground mt-1">Thank you — TalentOS AI has notified the recruitment team.</div>
          </div>
        ) : (
          <div className="border bg-white rounded-xl p-6 mt-6" data-testid="decision-form">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2">Did the interview happen?</div>
            <div className="flex gap-2">
              <button data-testid="happened-yes" onClick={() => setHappened(true)} className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors duration-150 ${happened ? "bg-[#0A251D] text-white border-[#0A251D]" : "hover:border-[#0A251D]"}`}>Yes</button>
              <button data-testid="happened-no" onClick={() => setHappened(false)} className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors duration-150 ${!happened ? "bg-[#0A251D] text-white border-[#0A251D]" : "hover:border-[#0A251D]"}`}>No / Cancelled</button>
            </div>

            {happened && (
              <>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mt-5 mb-2">Outcome</div>
                <div className="grid grid-cols-2 gap-2">
                  {STATUSES.map((s) => (
                    <button key={s.key} data-testid={`status-${s.key}`} onClick={() => setStatus(s.key)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border transition-colors duration-150 ${status === s.key ? `${s.color} text-white border-transparent` : "hover:border-[#0A251D]"}`}>
                      <s.icon size={16} /> {s.label}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-4 mt-5">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Rating (1-5)</label>
                    <input data-testid="rating-input" type="number" min="1" max="5" value={rating} onChange={(e) => setRating(e.target.value)} className="mt-1 w-full border px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Recommended CTC (LPA)</label>
                    <input data-testid="salary-input" type="number" value={salary} onChange={(e) => setSalary(e.target.value)} placeholder="optional" className="mt-1 w-full border px-3 py-2 text-sm" />
                  </div>
                </div>
              </>
            )}

            <div className="mt-5">
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Remarks {happened ? "" : "(please share reason)"}</label>
              <textarea data-testid="remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={4} className="mt-1 w-full border px-3 py-2 text-sm" />
            </div>

            <button data-testid="decision-submit" onClick={submit} disabled={saving}
              className="mt-6 w-full bg-[#E05A47] text-white py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#C74936] transition-colors duration-150 disabled:opacity-60">
              {saving ? "Submitting…" : "Submit decision"}
            </button>
          </div>
        )}

        <div className="mt-6 text-center"><PoweredByFundle /></div>
      </div>
    </div>
  );
}
